import React from "react";
import { Button, ButtonContainer, ButtonType, Dropdown, Form, OneColumn } from "@renta-apps/athenaeum-react-components";
import User from "@/models/server/User";
import WorkOrderType from "@/models/server/WorkOrderType";
import ConstructionSiteOrWarehouse from "@/models/server/ConstructionSiteOrWarehouse";
import Warehouse from "@/models/server/Warehouse";
import UserDefaultWorkOrderValues from "@/models/server/UserDefaultWorkOrderValues";
import SaveUserDefaultWorkOrderValuesRequest from "@/models/server/requests/SaveUserDefaultWorkOrderValuesRequest";
import Localizer from "@/localization/Localizer";

import styles from "./DefaultWorkOrderValuesTab.module.scss";

interface DefaultWorkOrderValuesTabProps {
    user: User | null;
    defaultValues: UserDefaultWorkOrderValues | null;
    workOrderTypes: WorkOrderType[];
    constructionSitesOrWarehouses: ConstructionSiteOrWarehouse[];
    managers: User[];
    warehouses: Warehouse[];
    onSave: (request: SaveUserDefaultWorkOrderValuesRequest) => Promise<void>;
    isPerformingAction: boolean;
}

interface DefaultWorkOrderValuesTabState {
    defaultValues: UserDefaultWorkOrderValues | null;
    isModified: boolean;
}

export default class DefaultWorkOrderValuesTab extends React.Component<DefaultWorkOrderValuesTabProps, DefaultWorkOrderValuesTabState> {
    
    state: DefaultWorkOrderValuesTabState = {
        defaultValues: null,
        isModified: false
    };

    public async componentDidMount(): Promise<void> {
        this.setState({ defaultValues: this.props.defaultValues });
    }

    public async componentDidUpdate(prevProps: DefaultWorkOrderValuesTabProps): Promise<void> {
        if (this.props.user?.id !== prevProps.user?.id || this.props.defaultValues !== prevProps.defaultValues) {
            this.setState({ defaultValues: this.props.defaultValues, isModified: false });
        }
    }

    private async onWorkOrderTypeChangeAsync(dropdown: Dropdown<WorkOrderType>): Promise<void> {
        await this.updateDefaultValues({
            workOrderTypeId: dropdown.selectedItem?.id,
            workOrderTypeName: dropdown.selectedItem?.name
        });
    }

    private async onConstructionSiteChangeAsync(dropdown: Dropdown<ConstructionSiteOrWarehouse>): Promise<void> {
        await this.updateDefaultValues({
            constructionSiteId: dropdown.selectedItem?.id,
            constructionSiteName: dropdown.selectedItem?.name
        });
    }

    private async onManagerChangeAsync(dropdown: Dropdown<User>): Promise<void> {
        const manager = dropdown.selectedItem;
        await this.updateDefaultValues({
            managerId: manager?.id,
            managerName: manager ? `${manager.firstname} ${manager.lastName}`.trim() : undefined
        });
    }

    private async onDepotWorkOrderTypeChangeAsync(dropdown: Dropdown<WorkOrderType>): Promise<void> {
        await this.updateDefaultValues({
            depotWorkOrderTypeId: dropdown.selectedItem?.id,
            depotWorkOrderTypeName: dropdown.selectedItem?.name
        });
    }

    private async onDepotConstructionSiteChangeAsync(dropdown: Dropdown<ConstructionSiteOrWarehouse>): Promise<void> {
        await this.updateDefaultValues({
            depotConstructionSiteId: dropdown.selectedItem?.id,
            depotConstructionSiteName: dropdown.selectedItem?.name
        });
    }

    private async onDepotManagerChangeAsync(dropdown: Dropdown<User>): Promise<void> {
        const manager = dropdown.selectedItem;
        await this.updateDefaultValues({
            depotManagerId: manager?.id,
            depotManagerName: manager ? `${manager.firstname} ${manager.lastName}`.trim() : undefined
        });
    }

    private async onDepotWarehouseChangeAsync(dropdown: Dropdown<Warehouse>): Promise<void> {
        await this.updateDefaultValues({
            depotWarehouseId: dropdown.selectedItem?.id,
            depotWarehouseName: dropdown.selectedItem?.name
        });
    }

    private async updateDefaultValues(updates: Partial<UserDefaultWorkOrderValues>): Promise<void> {
        const updatedValues = {
            ...this.state.defaultValues,
            ...updates
        };

        await this.setState({
            defaultValues: updatedValues,
            isModified: true
        });
    }

    private async saveAsync(): Promise<void> {
        if (!this.props.user || !this.state.defaultValues) {
            return;
        }

        const request: SaveUserDefaultWorkOrderValuesRequest = {
            userId: this.props.user.id,
            workOrderTypeId: this.state.defaultValues.workOrderTypeId,
            constructionSiteId: this.state.defaultValues.constructionSiteId,
            managerId: this.state.defaultValues.managerId,
            depotWorkOrderTypeId: this.state.defaultValues.depotWorkOrderTypeId,
            depotConstructionSiteId: this.state.defaultValues.depotConstructionSiteId,
            depotManagerId: this.state.defaultValues.depotManagerId,
            depotWarehouseId: this.state.defaultValues.depotWarehouseId
        };

        await this.props.onSave(request);
        await this.setState({ isModified: false });
    }

    private get isMounter(): boolean {
        return this.props.user?.isMounter || this.props.user?.isSubcontractorMounter || false;
    }

    public render(): React.ReactNode {
        if (!this.props.user) {
            return null;
        }

        const { isModified } = this.state;
        const { workOrderTypes, constructionSitesOrWarehouses, managers, warehouses, isPerformingAction } = this.props;

        return (
            <Form className={styles.defaultValuesTab}>
                <h5 className={styles.firstSection}>{Localizer.workOrdersPageTitle}</h5>
                <OneColumn>
                    <Dropdown<WorkOrderType>
                        items={workOrderTypes || []}
                        disabled={isPerformingAction}
                        label={Localizer.genericWorkOrderTypeLabel}
                        selectedItem={this.state.defaultValues ? workOrderTypes.find(t => t.id === this.state.defaultValues!.workOrderTypeId) : undefined}
                        onChange={async (dropdown) => await this.onWorkOrderTypeChangeAsync(dropdown)}
                    />
                </OneColumn>

                <OneColumn>
                    <Dropdown<ConstructionSiteOrWarehouse>
                        items={constructionSitesOrWarehouses || []}
                        disabled={isPerformingAction}
                        label={Localizer.genericConstructionSiteOrWarehouse}
                        selectedItem={this.state.defaultValues ? constructionSitesOrWarehouses.find(cs => cs.id === this.state.defaultValues!.constructionSiteId) : undefined}
                        onChange={async (dropdown) => await this.onConstructionSiteChangeAsync(dropdown)}
                    />
                </OneColumn>

                <OneColumn>
                    <Dropdown<User>
                        items={managers || []}
                        disabled={isPerformingAction}
                        label={Localizer.genericManager}
                        selectedItem={this.state.defaultValues ? managers.find(m => m.id === this.state.defaultValues!.managerId) : undefined}
                        onChange={async (dropdown) => await this.onManagerChangeAsync(dropdown)}
                    />
                </OneColumn>

                {this.isMounter && (
                    <>
                        <h5 className={styles.section}>{Localizer.depotWorkSectionTitle}</h5>

                        <OneColumn>
                            <Dropdown<WorkOrderType>
                                items={workOrderTypes || []}
                                disabled={isPerformingAction}
                                label={Localizer.genericWorkOrderTypeLabel}
                                selectedItem={this.state.defaultValues ? workOrderTypes.find(t => t.id === this.state.defaultValues!.depotWorkOrderTypeId) : undefined}
                                onChange={async (dropdown) => await this.onDepotWorkOrderTypeChangeAsync(dropdown)}
                            />
                        </OneColumn>

                        <OneColumn>
                            <Dropdown<ConstructionSiteOrWarehouse>
                                items={constructionSitesOrWarehouses.filter(cs => cs.isDepot === true) || []}
                                disabled={isPerformingAction}
                                label={Localizer.genericConstructionSiteOrWarehouse}
                                selectedItem={this.state.defaultValues ? constructionSitesOrWarehouses.find(cs => cs.id === this.state.defaultValues!.depotConstructionSiteId) : undefined}
                                onChange={async (dropdown) => await this.onDepotConstructionSiteChangeAsync(dropdown)}
                            />
                        </OneColumn>

                        <OneColumn>
                            <Dropdown<User>
                                items={managers || []}
                                disabled={isPerformingAction}
                                label={Localizer.genericManager}
                                selectedItem={this.state.defaultValues ? managers.find(m => m.id === this.state.defaultValues!.depotManagerId) : undefined}
                                onChange={async (dropdown) => await this.onDepotManagerChangeAsync(dropdown)}
                            />
                        </OneColumn>

                        <OneColumn>
                            <Dropdown<Warehouse>
                                items={warehouses || []}
                                disabled={isPerformingAction}
                                label={Localizer.genericWarehouse}
                                selectedItem={this.state.defaultValues ? warehouses.find(w => w.id === this.state.defaultValues!.depotWarehouseId) : undefined}
                                onChange={async (dropdown) => await this.onDepotWarehouseChangeAsync(dropdown)}
                            />
                        </OneColumn>
                    </>
                )}

                <ButtonContainer>
                    <Button
                        type={ButtonType.Primary}
                        disabled={!isModified || isPerformingAction}
                        onClick={() => this.saveAsync()}
                        label={Localizer.formSave}
                    />
                </ButtonContainer>
            </Form>
        );
    }
}